import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Typography, Input, Button, Avatar, Alert, Tag, Collapse } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined, ClearOutlined, DatabaseOutlined, LoadingOutlined } from '@ant-design/icons';
import { getAuthHeaders } from '../api';

const { TextArea } = Input;
const BASE_URL = process.env.REACT_APP_API_URL ?? '';

// ── Message types ─────────────────────────────────────────────────────────────
// Each message in `messages` state is one of:
//   { role: 'user',      content: string }
//   { role: 'assistant', content: string, toolEvents: [] }
//
// toolEvents is an array of { query, database, count, error } objects
// collected while the assistant was building the response.

function SqlBlock({ query, database, count, error }) {
  return (
    <div style={{
      background: '#1e1e1e', borderRadius: 6, marginBottom: 8, overflow: 'hidden',
      border: '1px solid #333', fontSize: 12,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 10px', background: '#2d2d2d', color: '#aaa',
      }}>
        <DatabaseOutlined style={{ color: '#52c41a' }} />
        <Tag color={database === 'readonly' ? 'blue' : 'green'} style={{ margin: 0, fontSize: 11 }}>
          {database}
        </Tag>
        {count != null && !error && (
          <span style={{ color: '#888' }}>{count} row{count !== 1 ? 's' : ''}</span>
        )}
        {error && <span style={{ color: '#ff6b6b' }}>{error}</span>}
      </div>
      <pre style={{
        margin: 0, padding: '8px 12px', color: '#d4d4d4',
        overflowX: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.5,
      }}>
        {query}
      </pre>
    </div>
  );
}

function AssistantMessage({ msg, isStreaming }) {
  const hasSql = msg.toolEvents && msg.toolEvents.length > 0;

  return (
    <div>
      {hasSql && (
        <Collapse
          ghost
          size="small"
          style={{ marginBottom: 8 }}
          items={[{
            key: '1',
            label: (
              <span style={{ color: '#888', fontSize: 12 }}>
                <DatabaseOutlined style={{ marginRight: 6 }} />
                {msg.toolEvents.length} database {msg.toolEvents.length === 1 ? 'query' : 'queries'}
              </span>
            ),
            children: msg.toolEvents.map((ev, i) => (
              <SqlBlock key={i} {...ev} />
            )),
          }]}
        />
      )}
      {msg.content
        ? <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
            dangerouslySetInnerHTML={{ __html: renderMarkdownTable(msg.content) }}
          />
        : isStreaming && <LoadingOutlined style={{ color: '#52c41a' }} />
      }
    </div>
  );
}

// Minimal markdown table → HTML (handles | col | col | rows)
function renderMarkdownTable(text) {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:2px 4px;border-radius:3px">$1</code>')
    .replace(/(^|\n)((\|[^\n]+\|\n?)+)/g, (_, pre, table) => {
      const rows = table.trim().split('\n').filter(r => !/^\s*\|[-:| ]+\|\s*$/.test(r));
      const html = rows.map((row, i) => {
        const cells = row.split('|').slice(1, -1).map(c => c.trim());
        const tag = i === 0 ? 'th' : 'td';
        return `<tr>${cells.map(c => `<${tag} style="border:1px solid #d9d9d9;padding:4px 10px;text-align:left">${c}</${tag}>`).join('')}</tr>`;
      }).join('');
      return `${pre}<table style="border-collapse:collapse;margin:8px 0;font-size:13px">${html}</table>`;
    })
    .replace(/\n/g, '<br/>');
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SidekickPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [streaming, setStreaming] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [error, setError]       = useState(null);
  const bottomRef  = useRef(null);
  const readerRef  = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, statusText]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;

    setInput('');
    setError(null);
    setStatusText('');

    const userMsg = { role: 'user', content: text };

    // Build wire format: only role+content for user/assistant (strip toolEvents)
    const wireHistory = [...messages, userMsg].map(({ role, content }) => ({ role, content }));

    setMessages(prev => [...prev, userMsg, { role: 'assistant', content: '', toolEvents: [] }]);
    setStreaming(true);
    setStatusText('Thinking…');

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${BASE_URL}/api/sidekick/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ messages: wireHistory }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buf = '';

      const updateLastMsg = (updater) => {
        setMessages(prev => {
          const next = [...prev];
          next[next.length - 1] = updater(next[next.length - 1]);
          return next;
        });
      };

      // Track pending SQL event waiting for its result
      let pendingSql = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (raw === '[DONE]') { setStatusText(''); break; }

          let evt;
          try { evt = JSON.parse(raw); } catch { continue; }

          switch (evt.type) {
            case 'delta':
              setStatusText('');
              updateLastMsg(m => ({ ...m, content: m.content + evt.content }));
              break;

            case 'status':
              setStatusText(evt.text);
              break;

            case 'sql':
              pendingSql = { query: evt.query, database: evt.database, count: null, error: null };
              setStatusText(`Querying ${evt.database} database…`);
              // Add placeholder tool event
              updateLastMsg(m => ({ ...m, toolEvents: [...(m.toolEvents || []), pendingSql] }));
              break;

            case 'sql_result':
              setStatusText('');
              if (pendingSql) {
                const finished = { ...pendingSql, count: evt.count, error: evt.error || null };
                pendingSql = null;
                updateLastMsg(m => {
                  const evts = [...(m.toolEvents || [])];
                  evts[evts.length - 1] = finished;
                  return { ...m, toolEvents: evts };
                });
              }
              break;

            case 'error':
              setError(evt.text);
              setStatusText('');
              // Remove empty assistant placeholder if content is blank
              setMessages(prev => {
                const last = prev[prev.length - 1];
                return last?.role === 'assistant' && !last.content
                  ? prev.slice(0, -1)
                  : prev;
              });
              break;

            default:
              break;
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
      setMessages(prev => {
        const last = prev[prev.length - 1];
        return last?.role === 'assistant' && !last.content ? prev.slice(0, -1) : prev;
      });
    } finally {
      readerRef.current = null;
      setStreaming(false);
      setStatusText('');
    }
  }, [input, messages, streaming]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleClear = () => {
    if (readerRef.current) readerRef.current.cancel();
    setMessages([]); setError(null); setStreaming(false);
    setInput(''); setStatusText('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          <RobotOutlined style={{ marginRight: 8, color: '#1677ff' }} />
          AI Sidekick
        </Typography.Title>
        <Button icon={<ClearOutlined />} onClick={handleClear} disabled={messages.length === 0 && !error}>
          New Chat
        </Button>
      </div>

      {error && (
        <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 12 }} />
      )}

      {/* Message thread */}
      <div style={{
        flex: 1, overflowY: 'auto', background: '#fff',
        border: '1px solid #f0f0f0', borderRadius: 8,
        padding: '16px 20px', marginBottom: 12,
      }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#aaa', marginTop: 60 }}>
            <RobotOutlined style={{ fontSize: 48, marginBottom: 12 }} />
            <div style={{ fontSize: 15 }}>Ask me anything about your QA data.</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>
              I can query your evaluation and call-log databases to answer questions.
            </div>
            <div style={{ fontSize: 12, marginTop: 4, color: '#bbb' }}>
              Press Enter to send · Shift+Enter for new line
            </div>
          </div>
        )}

        {messages.map((msg, idx) => {
          const isUser = msg.role === 'user';
          const isLast = idx === messages.length - 1;
          return (
            <div key={idx} style={{
              display: 'flex',
              flexDirection: isUser ? 'row-reverse' : 'row',
              alignItems: 'flex-start', gap: 10, marginBottom: 20,
            }}>
              <Avatar
                icon={isUser ? <UserOutlined /> : <RobotOutlined />}
                style={{ backgroundColor: isUser ? '#1677ff' : '#52c41a', flexShrink: 0 }}
              />
              <div style={{
                maxWidth: '80%',
                background: isUser ? '#e6f4ff' : '#f6ffed',
                border: `1px solid ${isUser ? '#91caff' : '#b7eb8f'}`,
                borderRadius: 8, padding: '10px 14px',
                fontSize: 14,
              }}>
                {isUser
                  ? <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{msg.content}</div>
                  : <AssistantMessage msg={msg} isStreaming={streaming && isLast} />
                }
              </div>
            </div>
          );
        })}

        {/* Status indicator between messages */}
        {streaming && statusText && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888', fontSize: 13, marginBottom: 12, marginLeft: 46 }}>
            <LoadingOutlined style={{ color: '#52c41a' }} />
            {statusText}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 8 }}>
        <TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question about your QA data…"
          autoSize={{ minRows: 1, maxRows: 6 }}
          disabled={streaming}
          style={{ flex: 1, fontSize: 14, borderRadius: 6 }}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={streaming}
          disabled={!input.trim()}
          style={{ height: 'auto', minHeight: 40, borderRadius: 6 }}
        >
          Send
        </Button>
      </div>
    </div>
  );
}
